import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Switch } from "../../ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import { Check } from "lucide-react";
import { LAYOUT_TEMPLATES } from "./configuratorConstants";

const SettingsDialog = ({
  open,
  onOpenChange,
  isDark,
  settings,
  setSettings,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={`sm:max-w-lg max-h-[85vh] overflow-y-auto ${isDark ? "bg-[#141414] border-white/[0.06]" : "bg-white"}`}
      >
        <DialogHeader>
          <DialogTitle className={isDark ? "text-white" : ""}>
            Configurator Instellingen
          </DialogTitle>
          <DialogDescription>
            Pas het uiterlijk, layout en gedrag van je configurator aan.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-6">
          {/* TEMPLATE */}
          <div className="space-y-2">
            <Label
              className={`text-xs font-bold uppercase tracking-wider ${isDark ? "text-gray-400" : "text-gray-500"}`}
            >
              Thema
            </Label>
            <div className="grid grid-cols-3 gap-2">
              {LAYOUT_TEMPLATES.map((template) => (
                <button
                  key={template.value}
                  onClick={() =>
                    setSettings((p) => ({
                      ...p,
                      layoutTemplate: template.value,
                    }))
                  }
                  className={`relative rounded-xl border-2 p-3 transition-all duration-200 text-left ${
                    settings.layoutTemplate === template.value
                      ? isDark
                        ? "border-white/20 shadow-md"
                        : "border-gray-400 shadow-md"
                      : isDark
                        ? "border-white/[0.06] hover:border-white/10"
                        : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <div
                    className={`w-full aspect-[4/3] rounded-lg mb-2 flex overflow-hidden ${
                      template.value === "dark"
                        ? "bg-gray-900"
                        : template.value === "light"
                          ? isDark
                            ? "bg-white/[0.06] border border-white/[0.06]"
                            : "bg-white border border-gray-200"
                          : isDark
                            ? "bg-white/[0.03] border border-white/[0.06]"
                            : "bg-gray-50 border border-gray-200"
                    }`}
                  >
                    <div
                      className={`flex-1 ${
                        template.value === "dark"
                          ? "bg-gray-800"
                          : template.value === "light"
                            ? isDark
                              ? "bg-white/10"
                              : "bg-gray-100"
                            : isDark
                              ? "bg-white/[0.03]"
                              : "bg-gray-50"
                      }`}
                    />
                    <div
                      className={`w-[40%] ${
                        template.value === "dark"
                          ? "bg-gray-900 border-l border-gray-700"
                          : template.value === "light"
                            ? isDark
                              ? "bg-white/[0.04] border-l border-white/[0.06]"
                              : "bg-white border-l border-gray-200"
                            : isDark
                              ? "bg-white/[0.04] border-l border-white/[0.06]"
                              : "bg-white border-l border-gray-100"
                      }`}
                    >
                      <div className="p-1 space-y-0.5">
                        {[...Array(3)].map((_, i) => (
                          <div
                            key={i}
                            className={`h-1 rounded-full ${
                              template.value === "dark"
                                ? "bg-gray-700"
                                : isDark
                                  ? "bg-white/10"
                                  : "bg-gray-200"
                            }`}
                            style={{ width: `${70 - i * 15}%` }}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                  <p
                    className={`text-xs font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
                  >
                    {template.label}
                  </p>
                  <p className="text-[10px] text-gray-400 leading-snug mt-0.5">
                    {template.description}
                  </p>
                  {settings.layoutTemplate === template.value && (
                    <div
                      className={`absolute top-1.5 right-1.5 h-5 w-5 rounded-full flex items-center justify-center ${isDark ? "bg-white" : "bg-gray-900"}`}
                    >
                      <Check className="h-3 w-3 text-white" />
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>

          <div
            className={`border-t ${isDark ? "border-white/[0.06]" : "border-gray-100"}`}
          />

          {/* KLEUREN */}
          <div className="space-y-3">
            <Label
              className={`text-xs font-bold uppercase tracking-wider ${isDark ? "text-gray-400" : "text-gray-500"}`}
            >
              Kleuren
            </Label>
            <div className="space-y-2">
              <Label className={`text-sm ${isDark ? "text-gray-300" : ""}`}>
                Achtergrondkleur
              </Label>
              <div className="flex items-center space-x-3">
                <input
                  type="color"
                  value={settings.backgroundColor}
                  onChange={(e) =>
                    setSettings((p) => ({
                      ...p,
                      backgroundColor: e.target.value,
                    }))
                  }
                  className="h-10 w-16 rounded cursor-pointer border-0"
                />
                <Input
                  value={settings.backgroundColor}
                  onChange={(e) =>
                    setSettings((p) => ({
                      ...p,
                      backgroundColor: e.target.value,
                    }))
                  }
                  className={`flex-1 ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white" : "border-gray-200"}`}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className={`text-sm ${isDark ? "text-gray-300" : ""}`}>
                Accent kleur
              </Label>
              <div className="flex items-center space-x-3">
                <input
                  type="color"
                  value={settings.accentColor}
                  onChange={(e) =>
                    setSettings((p) => ({
                      ...p,
                      accentColor: e.target.value,
                    }))
                  }
                  className="h-10 w-16 rounded cursor-pointer border-0"
                />
                <Input
                  value={settings.accentColor}
                  onChange={(e) =>
                    setSettings((p) => ({
                      ...p,
                      accentColor: e.target.value,
                    }))
                  }
                  className={`flex-1 ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white" : "border-gray-200"}`}
                />
              </div>
            </div>
          </div>

          <div
            className={`border-t ${isDark ? "border-white/[0.06]" : "border-gray-100"}`}
          />

          {/* LAYOUT / INDELING */}
          <div className="space-y-4">
            <Label
              className={`text-xs font-bold uppercase tracking-wider ${isDark ? "text-gray-400" : "text-gray-500"}`}
            >
              Indeling
            </Label>

            {/* Panel position */}
            <div className="space-y-2">
              <Label className={`text-sm ${isDark ? "text-gray-300" : ""}`}>
                Paneel positie
              </Label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  {
                    value: "right",
                    label: "Rechts",
                    desc: "Opties rechts, afbeelding links",
                  },
                  {
                    value: "left",
                    label: "Links",
                    desc: "Opties links, afbeelding rechts",
                  },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() =>
                      setSettings((p) => ({ ...p, panelPosition: opt.value }))
                    }
                    className={`rounded-lg border-2 p-3 text-left transition-all ${
                      settings.panelPosition === opt.value
                        ? isDark
                          ? "border-white/20 bg-white/[0.04]"
                          : "border-gray-400 bg-gray-50"
                        : isDark
                          ? "border-white/[0.06] hover:border-white/10"
                          : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <div
                      className={`flex gap-1 mb-2 h-8 rounded overflow-hidden border ${isDark ? "border-white/[0.06]" : "border-gray-200"}`}
                    >
                      <div
                        className={`${opt.value === "left" ? `w-[40%] ${isDark ? "bg-white/10" : "bg-gray-200"}` : `flex-1 ${isDark ? "bg-white/[0.04]" : "bg-gray-100"}`}`}
                      />
                      <div
                        className={`${opt.value === "left" ? `flex-1 ${isDark ? "bg-white/[0.04]" : "bg-gray-100"}` : `w-[40%] ${isDark ? "bg-white/10" : "bg-gray-200"}`}`}
                      />
                    </div>
                    <p
                      className={`text-xs font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
                    >
                      {opt.label}
                    </p>
                    <p className="text-[10px] text-gray-400">{opt.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Options layout */}
            <div className="space-y-2">
              <Label className={`text-sm ${isDark ? "text-gray-300" : ""}`}>
                Opties weergave
              </Label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  {
                    value: "list",
                    label: "Lijst",
                    desc: "Opties onder elkaar",
                  },
                  {
                    value: "grid",
                    label: "Grid",
                    desc: "Opties in 2 kolommen",
                  },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() =>
                      setSettings((p) => ({ ...p, optionsLayout: opt.value }))
                    }
                    className={`rounded-lg border-2 p-3 text-left transition-all ${
                      settings.optionsLayout === opt.value
                        ? isDark
                          ? "border-white/20 bg-white/[0.04]"
                          : "border-gray-400 bg-gray-50"
                        : isDark
                          ? "border-white/[0.06] hover:border-white/10"
                          : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <div
                      className={`mb-2 h-8 rounded overflow-hidden border p-1 ${isDark ? "border-white/[0.06]" : "border-gray-200"} ${opt.value === "grid" ? "grid grid-cols-2 gap-0.5" : "space-y-0.5"}`}
                    >
                      <div
                        className={`${isDark ? "bg-white/10" : "bg-gray-200"} rounded-sm h-full min-h-[3px]`}
                      />
                      <div
                        className={`${isDark ? "bg-white/10" : "bg-gray-200"} rounded-sm h-full min-h-[3px]`}
                      />
                      {opt.value === "list" && (
                        <div
                          className={`${isDark ? "bg-white/10" : "bg-gray-200"} rounded-sm h-full min-h-[3px]`}
                        />
                      )}
                    </div>
                    <p
                      className={`text-xs font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
                    >
                      {opt.label}
                    </p>
                    <p className="text-[10px] text-gray-400">{opt.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Image display mode */}
            <div className="space-y-2">
              <Label className={`text-sm ${isDark ? "text-gray-300" : ""}`}>
                Afbeelding weergave
              </Label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  {
                    value: "contain",
                    label: "Passend",
                    desc: "Volledig zichtbaar, met ruimte",
                  },
                  {
                    value: "cover",
                    label: "Vullen",
                    desc: "Vult het hele vlak",
                  },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() =>
                      setSettings((p) => ({ ...p, imageDisplay: opt.value }))
                    }
                    className={`rounded-lg border-2 p-3 text-left transition-all ${
                      settings.imageDisplay === opt.value
                        ? isDark
                          ? "border-white/20 bg-white/[0.04]"
                          : "border-gray-400 bg-gray-50"
                        : isDark
                          ? "border-white/[0.06] hover:border-white/10"
                          : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <div
                      className={`mb-2 h-8 rounded overflow-hidden border flex items-center justify-center ${isDark ? "border-white/[0.06] bg-white/[0.03]" : "border-gray-200 bg-gray-50"}`}
                    >
                      <div
                        className={`${isDark ? "bg-white/10" : "bg-gray-300"} rounded ${opt.value === "contain" ? "h-5 w-7" : "h-full w-full"}`}
                      />
                    </div>
                    <p
                      className={`text-xs font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
                    >
                      {opt.label}
                    </p>
                    <p className="text-[10px] text-gray-400">{opt.desc}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div
            className={`border-t ${isDark ? "border-white/[0.06]" : "border-gray-100"}`}
          />

          {/* AFREKENEN OPTIES */}
          <div className="space-y-3">
            <Label
              className={`text-xs font-bold uppercase tracking-wider ${isDark ? "text-gray-400" : "text-gray-500"}`}
            >
              Afrekenen
            </Label>
            <p className="text-[10px] text-gray-400">
              Kies welke afrekenmethoden beschikbaar zijn voor klanten
            </p>
            <div className="flex items-center justify-between">
              <div>
                <Label className={`text-sm ${isDark ? "text-gray-300" : ""}`}>
                  Configuratie opslaan
                </Label>
                <p className="text-[10px] text-gray-400">
                  Klant kan configuratie opslaan en ontvangt een link per e-mail
                  om later terug te keren
                </p>
              </div>
              <Switch
                checked={settings.checkoutOptions?.saveConfiguration !== false}
                onCheckedChange={(checked) =>
                  setSettings((p) => ({
                    ...p,
                    checkoutOptions: {
                      ...p.checkoutOptions,
                      saveConfiguration: checked,
                    },
                  }))
                }
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label className={`text-sm ${isDark ? "text-gray-300" : ""}`}>
                  Direct betalen (Mollie)
                </Label>
                <p className="text-[10px] text-gray-400">
                  Klant kan direct online betalen via Mollie
                </p>
              </div>
              <Switch
                checked={settings.checkoutOptions?.directPayment === true}
                onCheckedChange={(checked) =>
                  setSettings((p) => ({
                    ...p,
                    checkoutOptions: {
                      ...p.checkoutOptions,
                      directPayment: checked,
                    },
                  }))
                }
              />
            </div>
            {settings.checkoutOptions?.directPayment && (
              <div className="space-y-2 pl-1">
                <Label className={`text-sm ${isDark ? "text-gray-300" : ""}`}>
                  Mollie Profiel ID
                </Label>
                <p className="text-[10px] text-gray-400">
                  Het Mollie profiel ID voor betalingen (valt onder jouw Mollie
                  account)
                </p>
                <input
                  type="text"
                  value={settings.checkoutOptions?.mollieProfileId || ""}
                  readOnly
                  disabled
                  placeholder="Koppel Mollie via Bestellingen → Koppel met Mollie"
                  className={`w-full px-3 py-2 rounded-lg border text-sm cursor-not-allowed ${
                    isDark
                      ? "bg-white/[0.02] border-white/[0.06] text-gray-400 placeholder-gray-600"
                      : "bg-gray-50 border-gray-200 text-gray-500 placeholder-gray-400"
                  }`}
                />
                {settings.checkoutOptions?.mollieProfileId && (
                  <p
                    className={`text-[10px] mt-1 ${isDark ? "text-emerald-400" : "text-emerald-600"}`}
                  >
                    ✓ Automatisch ingevuld vanuit je Mollie koppeling
                  </p>
                )}
              </div>
            )}
          </div>

          <div
            className={`border-t ${isDark ? "border-white/[0.06]" : "border-gray-100"}`}
          />

          {/* WEERGAVE OPTIES */}
          <div className="space-y-3">
            <Label
              className={`text-xs font-bold uppercase tracking-wider ${isDark ? "text-gray-400" : "text-gray-500"}`}
            >
              Weergave
            </Label>
            <div className="flex items-center justify-between">
              <div>
                <Label className={`text-sm ${isDark ? "text-gray-300" : ""}`}>
                  Prijzen tonen
                </Label>
                <p className="text-[10px] text-gray-400">
                  Toon prijzen bij opties en totaal
                </p>
              </div>
              <Switch
                checked={settings.showPrices}
                onCheckedChange={(checked) =>
                  setSettings((p) => ({ ...p, showPrices: checked }))
                }
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label className={`text-sm ${isDark ? "text-gray-300" : ""}`}>
                  Afbeeldingen tonen
                </Label>
                <p className="text-[10px] text-gray-400">
                  Toon product afbeeldingen
                </p>
              </div>
              <Switch
                checked={settings.showImages}
                onCheckedChange={(checked) =>
                  setSettings((p) => ({ ...p, showImages: checked }))
                }
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label className={`text-sm ${isDark ? "text-gray-300" : ""}`}>
                  Thumbnails tonen
                </Label>
                <p className="text-[10px] text-gray-400">
                  Toon thumbnail strip onder de afbeelding
                </p>
              </div>
              <Switch
                checked={settings.showThumbnails !== false}
                onCheckedChange={(checked) =>
                  setSettings((p) => ({ ...p, showThumbnails: checked }))
                }
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label className={`text-sm ${isDark ? "text-gray-300" : ""}`}>
                  Samenvatting pagina
                </Label>
                <p className="text-[10px] text-gray-400">
                  Toon een overzichtspagina op het einde
                </p>
              </div>
              <Switch
                checked={settings.summaryEnabled}
                onCheckedChange={(checked) =>
                  setSettings((p) => ({ ...p, summaryEnabled: checked }))
                }
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label className={`text-sm ${isDark ? "text-gray-300" : ""}`}>
                  Terug navigatie
                </Label>
                <p className="text-[10px] text-gray-400">
                  Laat gebruikers tussen stappen navigeren
                </p>
              </div>
              <Switch
                checked={settings.allowBackNavigation}
                onCheckedChange={(checked) =>
                  setSettings((p) => ({
                    ...p,
                    allowBackNavigation: checked,
                  }))
                }
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button
            onClick={() => onOpenChange(false)}
            className={
              isDark
                ? "bg-white text-black hover:bg-gray-200"
                : "bg-gray-900 text-white hover:bg-gray-800"
            }
          >
            Sluiten
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SettingsDialog;
